/* eslint-disable react-hooks/exhaustive-deps */
import { faArrowRight, faPlus } from "@fortawesome/free-solid-svg-icons";
import React, { useContext, useEffect, useState } from "react";
import { Badge, Button, Card, Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { AppRulesContext } from "../../App";
import ITRFilingApi from "../../api/ITRFilingApi";
import FASvgIcon from "../../components/FASvgIcon";
import GuardedComponent from "../../components/GuardedComponent";
import SvgImage from "../../components/SvgImage";
import { ButtonSpinner, LoadingIndicator } from "../../components/placeholders";
import { appConstants } from "../../config/app-constants";
import { EToastvariant } from "../../config/interfaces";
import { useAppDispatch, useAppSelector } from "../../redux/Store";
import {
	ITRFilingSelector,
	fetchRecentEfiling,
	setCurrentFilingUUID
} from "../../redux/slice/ITRFilingSlice";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../services";
import { FilingFormInitValues } from "../itr-filing/efiling.config";

type Props = {};

const Home: React.FC<Props> = (props: Props) => {
	const navigate = useNavigate();

	const dispatch = useAppDispatch();

	const { RFLoadingStatus, currentFilingUUID, currentFilingData } =
		useAppSelector(ITRFilingSelector);

	const { rulesSvc } = useContext(AppRulesContext);

	const [isCreating, setIsCreating] = useState<boolean>(false);

	useEffect(() => {
		if (rulesSvc.createAndUpdateEFiling) {
			dispatch(fetchRecentEfiling());
		}
	}, []);

	const handleCreateEmptyRecord = async () => {
		try {
			setIsCreating(() => true);
			const filingData = {
				efilingData: FilingFormInitValues,
				name: "",
				pan: "",
				assessmentYear: "2024-25"
			};

			const filingApi = ITRFilingApi.getInstance();
			const { uuid } = await filingApi.createITRFiling(filingData);

			dispatch(setCurrentFilingUUID(uuid));
			navigate(`/itr-filings/${uuid}`);
		} catch (error: any) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		} finally {
			setIsCreating(() => false);
		}
	};

	return (
		<Container className="w-970">
			{RFLoadingStatus === "PENDING" ? (
				<LoadingIndicator />
			) : (
				<>
					<Card bg="white" className="home-card">
						<Card.Body>
							<Row className="align-items-center gap-5 justify-content-center justify-content-md-start">
								<Col sm="auto">
									<div className="img-wrap text-center">
										<img
											className="img-fluid"
											src={`${appConstants.basePath}/assets/images/start-filing.svg`}
											alt="start filling"
										/>
									</div>
									{/* <SvgImage imgId="start-filing-illustrator" /> */}
								</Col>
								<Col sm={12} md>
									{currentFilingUUID ? (
										<div className="card-content text-center text-md-start">
											<h1 className="fw-bold mb-4">Continue your recent filing</h1>
											<h5 className="mb-4">
												<span>Name: {currentFilingData?.name || "-"} | </span>
												<span>AY: {currentFilingData?.assessmentYear || "-"} | </span>
												<span>PAN: {currentFilingData?.pan || "-"}</span>
											</h5>
											<div className="btns-wrap">
												<Button
													size="lg"
													className="shadow-lg mx-0"
													onClick={() => navigate(`/itr-filings/${currentFilingUUID}`)}
												>
													Continue Filing <FASvgIcon icon={faArrowRight} />
												</Button>
												<span className="primary-para">OR</span>
												<Button
													size="lg"
													type="button"
													variant="secondary"
													className="shadow-lg mx-0"
													onClick={() => handleCreateEmptyRecord()}
													disabled={isCreating}
												>
													{isCreating ? (
														<ButtonSpinner />
													) : (
														<>
															<FASvgIcon icon={faPlus} /> New Filing
														</>
													)}
												</Button>
											</div>
										</div>
									) : (
										<div className="card-content text-center text-md-start">
											<h1 className="fw-bold mb-4">Get started with your first filing.</h1>
											<h5 className="mb-4">
												File your ITR with these steps. Self assessment is completely free! If you
												need any help, our tax filing experts are available at your disposal.*
											</h5>
											<h5>Start today with a hassle free filing with pre-filled details.</h5>
										</div>
									)}
								</Col>
							</Row>
							<div className="abs-content">
								{/* <SvgImage imgId="steps-illustrator" /> */}
								<Row className="cards-grid gap-xl-4">
									<Col md={6} xl>
										<Card className="home-step-card h-100" bg="primaryLight">
											<Card.Body>
												<Badge pill bg="primaryLight" className="text-primary">
													1
												</Badge>
												<div>
													<SvgImage imgId="personal-details" />
													<h5 className="fw-bold">Add Personal Details</h5>
												</div>
											</Card.Body>
										</Card>
									</Col>
									<Col md={6} xl>
										<Card className="home-step-card h-100" bg="infoLight">
											<Card.Body>
												<Badge pill bg="infoLight" className="text-info">
													2
												</Badge>
												<div>
													<SvgImage imgId="income-details" />
													<h5 className="fw-bold">Add Income details</h5>
												</div>
											</Card.Body>
										</Card>
									</Col>
									<Col md={6} xl>
										<Card className="home-step-card h-100" bg="warningLight">
											<Card.Body>
												<Badge pill bg="warningLight" className="text-warning">
													3
												</Badge>
												<div>
													<SvgImage imgId="add-deduction" />
													<h5 className="fw-bold">Add Deductions</h5>
												</div>
											</Card.Body>
										</Card>
									</Col>
									<Col md={6} xl>
										<Card className="home-step-card h-100" bg="success">
											<Card.Body>
												<Badge pill bg="success" className="text-white">
													4
												</Badge>
												<div>
													<SvgImage imgId="review-file" />
													<h5 className="fw-bold">Review and file tax</h5>
												</div>
											</Card.Body>
										</Card>
									</Col>
								</Row>
							</div>
							{!currentFilingUUID && (
								<GuardedComponent rules={rulesSvc.createAndUpdateEFiling} behavior="hide">
									<div className="text-center mb-4">
										<Button
											size="lg"
											className="file-btn shadow-lg"
											onClick={() => handleCreateEmptyRecord()}
										>
											{isCreating ? (
												<ButtonSpinner />
											) : (
												<>
													<FASvgIcon icon={faPlus} /> Start filing now!
												</>
											)}
										</Button>
										<em className="d-block leading-para text-greyMedium mt-5">
											* Actual tax filing and expert assistance is chargeable.
										</em>
									</div>
								</GuardedComponent>
							)}
						</Card.Body>
					</Card>
					{/* <GuardedComponent rules={rulesSvc.createAndUpdateEFiling} behavior="hide">
            <>
              <div className="btns-wrap">
                <Button
                  size="lg"
                  type="button"
                  variant="outline-greyDark"
                  className="btn-w-160"
                  onClick={() => handleCreateEmptyRecord()}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <ButtonSpinner />
                  ) : (
                    <>
                      <FASvgIcon icon={faPlus} /> New Filing
                    </>
                  )}
                </Button>
                <Button
                  size="lg"
                  type="button"
                  variant="secondary"
                  className="btn-w-160 text-white"
                  onClick={() => navigate("/itr-filings")}
                >
                  View All Filings <FASvgIcon icon={faArrowRight} />
                </Button>
              </div>
            </>
          </GuardedComponent> */}
				</>
			)}
		</Container>
	);
};

export default Home;
